import { useMemo } from "react";
import { Search } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";

import { getCampoLabel } from "../helpers";
import { FilterField } from "./FilterField";
import type { DateRangeState } from "../types";

interface FilterFieldsPanelProps {
  filters: Record<string, any>;
  selected: Record<string, any[]>;
  dateRanges: Record<string, DateRangeState>;
  searches: Record<string, string>;
  fieldSearch: string;
  onFieldSearchChange: (value: string) => void;
  onToggle: (group: string, option: any, booleanMode?: boolean) => void;
  onBulkToggle: (group: string, optionsToToggle: any[], selectAll: boolean) => void;
  onSearchChange: (group: string, value: string) => void;
  onDateChange: (group: string, side: "from" | "to", value: string) => void;
  onYearChange: (group: string, year: string) => void;
}

/* Aba "Personalizado": busca entre campos + accordion com cada filtro. */
export function FilterFieldsPanel({
  filters,
  selected,
  dateRanges,
  searches,
  fieldSearch,
  onFieldSearchChange,
  onToggle,
  onBulkToggle,
  onSearchChange,
  onDateChange,
  onYearChange,
}: FilterFieldsPanelProps) {
  // Grupos visíveis, filtrados pela busca entre campos e ordenados pelo label.
  const visibleGroups = useMemo(() => {
    const term = fieldSearch.trim().toLowerCase();
    return Object.keys(filters)
      .filter((g) => !term || getCampoLabel(filters, g).toLowerCase().includes(term))
      .sort((a, b) => getCampoLabel(filters, a).localeCompare(getCampoLabel(filters, b)));
  }, [filters, fieldSearch]);

  return (
    <div className="space-y-4 px-6 py-6">
      {Object.keys(filters).length > 4 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar filtro..."
            value={fieldSearch}
            onChange={(e) => onFieldSearchChange(e.target.value)}
            className="h-9 pl-8 text-xs"
          />
        </div>
      )}
      <Accordion type="multiple" className="w-full space-y-2">
        {visibleGroups.map((group) => {
          const field = filters[group];
          const hasValue = selected[group]?.length > 0 || dateRanges[group]?.from;

          return (
            <AccordionItem
              value={group}
              key={group}
              className="rounded-lg border border-border bg-card shadow-sm"
            >
              <AccordionTrigger className="px-4 py-3 text-sm hover:no-underline">
                <div className="flex items-center gap-2">
                  <span className={hasValue ? "font-semibold text-primary" : "text-foreground"}>
                    {getCampoLabel(filters, group)}
                  </span>
                  {hasValue && <span className="h-2 w-2 rounded-full bg-primary" />}
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-t border-dashed border-border/50 px-4 pb-4 pt-2">
                <FilterField
                  group={group}
                  field={field}
                  searches={searches}
                  selected={selected}
                  dateRanges={dateRanges}
                  onToggle={onToggle}
                  onBulkToggle={onBulkToggle}
                  onSearchChange={onSearchChange}
                  onDateChange={onDateChange}
                  onYearChange={onYearChange}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
        {visibleGroups.length === 0 && (
          <div className="py-6 text-center text-xs text-muted-foreground">
            Nenhum filtro encontrado
          </div>
        )}
      </Accordion>
    </div>
  );
}
